import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ChevronRight, MoreHorizontal, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type AdminModuleAction, type AdminModuleOverview, type AdminModulePackage, type ModulePackageInput, type ModulePhase } from "@/lib/api";
import { priceText } from "./Modules";
import { AdminBundlesSection } from "@/components/ModuleBundles";
import { intlLocale } from "@/lib/i18n";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "");
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
const PHASE: Record<ModulePhase, { label: string; tone: "info" | "success" | "neutral" | "warning" }> = {
  trial: { label: "Testphase", tone: "info" },
  paid: { label: "kostenpflichtig", tone: "success" },
  free: { label: "kostenlos", tone: "neutral" },
  ended: { label: "beendet", tone: "neutral" },
  none: { label: "—", tone: "neutral" },
};
const comma = (v: string) => v.replace(".", ",");

/** Logiful intern: Pakete je Modul (Preis, Testphase, Funktionen) und Module aller Kunden */
export function AdminModulesPage() {
  const { me } = useTenant();
  const admin = me.user.staffRole === "staff_admin";
  const q = useQuery({ queryKey: ["admin-modules"], queryFn: api.admin.modules });
  return (
    <div className="pb-16">
      <PageHeader title="Module & Preise" description="Pakete je Modul mit Preis, Testphase und enthaltenen Funktionen – je Kunde Paket, Testphase, kostenlos, Sonderpreis oder beenden." />
      <div className="px-4 md:px-8 space-y-6 max-w-6xl">
        {q.data && <AdminBundlesSection modules={q.data} admin={admin} />}
        {q.isLoading ? <Skeleton className="h-80" /> : q.data?.map((m) => <ModuleAdminCard key={m.key} m={m} admin={admin} />)}
      </div>
    </div>
  );
}

function ModuleAdminCard({ m, admin }: { m: AdminModuleOverview; admin: boolean }) {
  const [adding, setAdding] = useState(false);
  const [addingPkg, setAddingPkg] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const live = m.packages.filter((p) => !p.archived);
  const archived = m.packages.filter((p) => p.archived);
  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-[15px]">{m.name}</h2>
          <p className="text-[12.5px] text-fg-muted">{m.counts.paid} kostenpflichtig · {m.counts.trial} in Testphase · {m.counts.free} kostenlos</p>
        </div>
        {admin && <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setAddingPkg(true)}>Paket hinzufügen</Button>}
      </div>

      <div className="p-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {live.map((p) => <PackageEditor key={p.id} m={m} p={p} admin={admin} />)}
        {addingPkg && <PackageEditor m={m} admin={admin} onDone={() => setAddingPkg(false)} />}
      </div>
      {archived.length > 0 && (
        <div className="px-5 pb-3">
          <button className="text-[12px] text-fg-muted underline underline-offset-2" onClick={() => setShowArchived(!showArchived)}>{showArchived ? "Archivierte Pakete ausblenden" : `${archived.length} archivierte Pakete anzeigen`}</button>
          {showArchived && <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">{archived.map((p) => <PackageEditor key={p.id} m={m} p={p} admin={admin} />)}</div>}
        </div>
      )}

      <div className="border-t border-border">
        <div className="px-5 py-2.5 flex items-center gap-2">
          <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">Kunden</h3>
          {admin && !adding && <Button size="sm" variant="ghost" className="ml-auto" icon={<Plus className="size-3.5" />} onClick={() => setAdding(true)}>Kunde hinzufügen</Button>}
        </div>
        {adding && <AddTenant m={m} onDone={() => setAdding(false)} />}
        {m.subscriptions.length === 0 ? (
          <p className="px-5 pb-4 text-[13px] text-fg-subtle">Noch kein Kunde.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11.5px] text-fg-subtle text-left">
                <tr>{["Kunde", "Paket", "Stand", "Hinweis", ""].map((h) => <th key={h} className="h-8 px-5 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {m.subscriptions.map((s) => (
                  <tr key={s.tenantId} className="border-t border-border align-top">
                    <td className="px-5 py-2.5"><span className="font-medium">{s.tenantName}</span>{s.customerNumber && <span className="block text-[11.5px] text-fg-subtle tabular-nums">Kd-Nr. {s.customerNumber}</span>}</td>
                    <td className="px-5 py-2.5">
                      <span className="font-medium">{s.packageName ?? "—"}</span>
                      {s.pendingPackageName && <span className="block text-[11.5px] text-fg-subtle">→ {s.pendingPackageName} ab {day(s.pendingFrom)}</span>}
                    </td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      <Badge tone={PHASE[s.phase].tone}>{PHASE[s.phase].label}</Badge>
                      {s.special && <span className="ml-1.5 text-[11.5px] text-success">Sonderpreis</span>}
                      <span className="block text-[11.5px] text-fg-subtle">
                        {s.phase === "trial" && `bis ${day(s.trialEndsAt)}`}
                        {s.cancelAt && `${s.phase === "trial" ? " · " : ""}endet ${day(s.cancelAt)}`}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-[12px] text-fg-muted max-w-[260px]">{s.note ?? ""}</td>
                    <td className="px-5 py-2.5 text-right">{admin && <TenantActions m={m} s={s} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Paket bearbeiten oder neu anlegen: Name, Beschreibung, Funktionen, Preise, Testphase, buchbar, archivieren */
function PackageEditor({ m, p, admin, onDone }: { m: AdminModuleOverview; p?: AdminModulePackage; admin: boolean; onDone?: () => void }) {
  const qc = useQueryClient();
  const initial: ModulePackageInput = p
    ? { name: p.name, description: p.description, features: p.features, bookable: p.bookable, trialDays: p.trialDays, baseFee: comma(p.baseFee), unitFee: comma(p.unitFee), freeUnits: p.freeUnits, billingBaseArticleId: p.billingBaseArticleId, billingUnitArticleId: p.billingUnitArticleId, sortOrder: p.sortOrder, archived: p.archived }
    : { name: "", description: null, features: [], bookable: false, trialDays: 14, baseFee: "0", unitFee: "0", freeUnits: 0, billingBaseArticleId: null, billingUnitArticleId: null };
  const [f, setF] = useState<ModulePackageInput>(initial);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const save = useMutation({
    mutationFn: async (body: ModulePackageInput): Promise<void> => void (p ? await api.admin.updateModulePackage(p.id, body) : await api.admin.createModulePackage(m.key, body)),
    onSuccess: (_r, body) => {
      toast.success(p ? (body.archived && !p.archived ? "Paket archiviert" : "Paket gespeichert") : "Paket angelegt", { description: p ? "Gilt sofort für alle Kunden mit diesem Paket (Funktionen) bzw. ohne Sonderpreis (Preise)." : undefined });
      void qc.invalidateQueries({ queryKey: ["admin-modules"] });
      onDone?.();
    },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const toggle = (feat: string) => setF({ ...f, features: f.features.includes(feat) ? f.features.filter((x) => x !== feat) : [...f.features, feat] });
  const lbl = "text-[11.5px] font-medium text-fg-muted";
  const num = (v: string) => v.replace(/\D/g, "");
  return (
    <div className={cx("rounded-xl border p-3.5 flex flex-col gap-2.5", p?.archived ? "border-dashed border-border opacity-70" : "border-border")}>
      <div className="flex items-center gap-2">
        <Input value={f.name} disabled={!admin} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Paketname, z. B. Pro" className="h-8 font-semibold" />
        {f.bookable ? <Badge tone="success">buchbar</Badge> : <Badge>intern</Badge>}
      </div>
      <Input value={f.description ?? ""} disabled={!admin} onChange={(e) => setF({ ...f, description: e.target.value || null })} placeholder="Kurzbeschreibung für Kunden" className="h-8 text-[12.5px]" />
      <div>
        <p className={lbl}>Enthaltene Funktionen</p>
        <ul className="mt-1 space-y-1">
          {Object.entries(m.featureLabels).map(([k, label]) => (
            <li key={k}>
              <label className="flex items-start gap-2 text-[12.5px] cursor-pointer select-none">
                <input type="checkbox" className="mt-0.5 size-3.5 accent-current" disabled={!admin} checked={f.features.includes(k)} onChange={() => toggle(k)} />
                {label}
              </label>
            </li>
          ))}
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label><span className={lbl}>Grundgebühr / Monat €</span><Input className="h-8 mt-0.5 tabular-nums" value={f.baseFee} disabled={!admin} onChange={(e) => setF({ ...f, baseFee: e.target.value })} /></label>
        <label><span className={lbl}>je {m.unit} €</span><Input className="h-8 mt-0.5 tabular-nums" value={f.unitFee} disabled={!admin} onChange={(e) => setF({ ...f, unitFee: e.target.value })} /></label>
        <label><span className={lbl}>Inklusive je Monat</span><Input className="h-8 mt-0.5 tabular-nums" inputMode="numeric" value={String(f.freeUnits)} disabled={!admin} onChange={(e) => setF({ ...f, freeUnits: Number(num(e.target.value)) || 0 })} /></label>
        <label><span className={lbl}>Testphase (Tage)</span><Input className="h-8 mt-0.5 tabular-nums" inputMode="numeric" value={String(f.trialDays)} disabled={!admin} onChange={(e) => setF({ ...f, trialDays: Math.min(90, Number(num(e.target.value)) || 0) })} /></label>
        <label><span className={lbl}>Artikel-ID Grundgebühr</span><Input className="h-8 mt-0.5 tabular-nums" inputMode="numeric" placeholder="Abrechnung" value={f.billingBaseArticleId ?? ""} disabled={!admin} onChange={(e) => setF({ ...f, billingBaseArticleId: num(e.target.value) ? Number(num(e.target.value)) : null })} /></label>
        <label><span className={lbl}>Artikel-ID Stückpreis</span><Input className="h-8 mt-0.5 tabular-nums" inputMode="numeric" placeholder="Abrechnung" value={f.billingUnitArticleId ?? ""} disabled={!admin} onChange={(e) => setF({ ...f, billingUnitArticleId: num(e.target.value) ? Number(num(e.target.value)) : null })} /></label>
      </div>
      <p className="text-[11.5px] text-fg-muted">Kunden sehen: <span className="text-fg">{priceText({ baseFee: Number(f.baseFee.replace(",", ".")) || 0, unitFee: Number(f.unitFee.replace(",", ".")) || 0, freeUnits: f.freeUnits, discountPct: 0 }, m.unit)}</span></p>
      {admin && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <label className="flex items-center gap-1.5 text-[12.5px] cursor-pointer select-none mr-auto">
            <input type="checkbox" className="size-3.5 accent-current" checked={f.bookable} onChange={(e) => setF({ ...f, bookable: e.target.checked })} />
            Kunden können buchen
          </label>
          {p && (
            <Button size="sm" variant="ghost" icon={<Archive className="size-3.5" />} onClick={() => (p.archived ? save.mutate({ ...f, archived: false }) : window.confirm(`Paket „${p.name}“ archivieren? Nicht mehr buchbar; bestehende Kunden behalten es.`) && save.mutate({ ...f, archived: true }))}>
              {p.archived ? "Reaktivieren" : "Archivieren"}
            </Button>
          )}
          {onDone && <Button size="sm" variant="ghost" onClick={onDone}>Abbrechen</Button>}
          <Button size="sm" variant="primary" disabled={!f.name.trim() || (!dirty && !!p)} loading={save.isPending} onClick={() => save.mutate(f)}>{p ? "Speichern" : "Anlegen"}</Button>
        </div>
      )}
    </div>
  );
}

function useTenantModuleAction(onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { tenantId: string; key: AdminModuleOverview["key"]; a: AdminModuleAction }) => api.admin.setTenantModule(v.tenantId, v.key, v.a),
    onSuccess: () => { toast.success("Gespeichert"); void qc.invalidateQueries({ queryKey: ["admin-modules"] }); void qc.invalidateQueries({ queryKey: ["modules"] }); onDone?.(); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
}

/** Kunde zu einem Modul hinzufügen: Paket + Testphase (Tage), kostenlos oder kostenpflichtig */
function AddTenant({ m, onDone }: { m: AdminModuleOverview; onDone: () => void }) {
  const tenants = useQuery({ queryKey: ["admin-tenants"], queryFn: api.admin.tenants });
  const live = m.packages.filter((p) => !p.archived);
  const [tenantId, setTenantId] = useState("");
  const [packageId, setPackageId] = useState(live.at(-1)?.id ?? "");
  const [mode, setMode] = useState<"trial" | "free" | "paid">("trial");
  const [days, setDays] = useState(String(live.at(-1)?.trialDays || 14));
  const act = useTenantModuleAction(onDone);
  const taken = useMemo(() => new Set(m.subscriptions.map((s) => s.tenantId)), [m.subscriptions]);
  const sel = "h-9 rounded-lg border border-border bg-surface px-2 text-[13px]";
  return (
    <div className="mx-5 mb-3 rounded-xl border border-border p-3 flex flex-wrap items-end gap-2">
      <label className="min-w-[220px] flex-1"><span className="text-[12px] text-fg-muted">Kunde</span>
        <select className={cx(sel, "w-full mt-1")} value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
          <option value="">– auswählen –</option>
          {(tenants.data ?? []).filter((t) => !taken.has(t.id)).map((t) => <option key={t.id} value={t.id}>{t.name}{t.customerNumber ? ` · Kd-Nr. ${t.customerNumber}` : ""}</option>)}
        </select>
      </label>
      <label><span className="text-[12px] text-fg-muted">Paket</span>
        <select className={cx(sel, "block mt-1")} value={packageId} onChange={(e) => setPackageId(e.target.value)}>
          {live.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label><span className="text-[12px] text-fg-muted">Art</span>
        <select className={cx(sel, "block mt-1")} value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
          <option value="trial">Testphase</option>
          <option value="free">kostenlos freigeben</option>
          <option value="paid">kostenpflichtig</option>
        </select>
      </label>
      {mode === "trial" && <label className="w-24"><span className="text-[12px] text-fg-muted">Tage</span><Input className="mt-1 tabular-nums" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ""))} /></label>}
      <Button variant="ghost" onClick={onDone}>Abbrechen</Button>
      <Button variant="primary" disabled={!tenantId || !packageId || (mode === "trial" && !Number(days))} loading={act.isPending} onClick={() => act.mutate({ tenantId, key: m.key, a: mode === "trial" ? { action: "trial", days: Number(days), packageId } : { action: mode, packageId } })}>Hinzufügen</Button>
    </div>
  );
}

/** Aktionen je Kunde (Logiful-Admin) */
function TenantActions({ m, s }: { m: AdminModuleOverview; s: AdminModuleOverview["subscriptions"][number] }) {
  const act = useTenantModuleAction();
  const run = (a: AdminModuleAction, question?: string) => (!question || window.confirm(question)) && act.mutate({ tenantId: s.tenantId, key: m.key, a });
  const item = "px-3 py-2 text-[13px] rounded-md cursor-pointer outline-none data-[highlighted]:bg-surface-2 flex items-center";
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={`Modul für ${s.tenantName} verwalten`}><MoreHorizontal className="size-4" /></button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} className="z-50 min-w-[250px] rounded-xl border border-border bg-surface p-1 shadow-xl">
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className={item}>Paket ändern (sofort)<ChevronRight className="size-3.5 ml-auto" /></DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent sideOffset={4} className="z-50 min-w-[200px] rounded-xl border border-border bg-surface p-1 shadow-xl">
                {m.packages.filter((p) => !p.archived).map((p) => (
                  <DropdownMenu.Item key={p.id} disabled={p.id === s.packageId} className={cx(item, p.id === s.packageId && "opacity-50")} onSelect={() => run({ action: "package", packageId: p.id }, `„${s.tenantName}“ ab sofort auf „${p.name}“ umstellen?`)}>{p.name}</DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          <DropdownMenu.Item className={item} onSelect={() => { const d = window.prompt("Testphase – wie viele Tage ab heute?", "14"); if (d && Number(d) > 0) run({ action: "trial", days: Number(d) }); }}>Testphase vergeben / verlängern …</DropdownMenu.Item>
          {s.phase !== "free" && <DropdownMenu.Item className={item} onSelect={() => run({ action: "free" }, `„${s.tenantName}“ kostenlos freigeben? Es wird nichts mehr berechnet.`)}>Kostenlos freigeben</DropdownMenu.Item>}
          {s.phase !== "paid" && <DropdownMenu.Item className={item} onSelect={() => run({ action: "paid" }, `„${s.tenantName}“ ab heute kostenpflichtig schalten?`)}>Ab heute kostenpflichtig</DropdownMenu.Item>}
          <DropdownMenu.Item className={item} onSelect={() => {
            const base = window.prompt("Sonderpreis Grundgebühr / Monat in € (leer = Paketpreis)", "");
            if (base === null) return;
            const unit = window.prompt("Sonderpreis je Einheit in € (leer = Paketpreis)", "");
            if (unit === null) return;
            const override = { ...(base.trim() ? { baseFee: base.trim() } : {}), ...(unit.trim() ? { unitFee: unit.trim() } : {}) };
            run({ action: "price", override: Object.keys(override).length ? override : null });
          }}>Sonderpreis …</DropdownMenu.Item>
          <DropdownMenu.Item className={item} onSelect={() => { const n = window.prompt("Notiz (nur für Logiful sichtbar)", s.note ?? ""); if (n !== null) run({ action: "note", note: n.trim() || null }); }}>Notiz …</DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          {s.cancelAt ? (
            <DropdownMenu.Item className={item} onSelect={() => run({ action: "undo_cancel" })}>Kündigung zurücknehmen</DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item className={item} onSelect={() => run({ action: "end_at_month_end" }, `Modul für „${s.tenantName}“ zum Monatsende beenden?`)}>Zum Monatsende beenden</DropdownMenu.Item>
          )}
          <DropdownMenu.Item className={cx(item, "text-danger")} onSelect={() => run({ action: "end_now" }, `Modul für „${s.tenantName}“ sofort beenden? Die Funktion ist dann sofort gesperrt.`)}>Sofort beenden</DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
